/**
 * Centralized Unsplash image registry.
 *
 * Every photo below is licensed under the free Unsplash License
 * (https://unsplash.com/license) — use is permitted for commercial and
 * non-commercial purposes without attribution, though providing attribution
 * is appreciated and is what `photographerName` / `unsplashUrl` enable.
 *
 * Adding a new photo:
 *   1. Find a free photo on unsplash.com (skip anything marked Unsplash+).
 *   2. Read the JSON metadata at `https://unsplash.com/napi/photos/<short-id>`
 *      — the stable CDN hash is the `photo-xxxx-xxxx` segment of `urls.raw`.
 *   3. Add a new entry here; reference it by its key from `cheeseries.ts`
 *      or `accommodations.ts`.
 */

export interface UnsplashPhoto {
  /** Short id used in unsplash.com/photos/<id> URLs. */
  unsplashId: string;
  /** Stable CDN path segment, e.g. `photo-1234567890-abcdef`. */
  cdnHash: string;
  photographerName: string;
  photographerUsername: string;
  altText: string;
}

const photo = (p: UnsplashPhoto) => p;

/**
 * Thematic pool. Keys are arbitrary but descriptive — they are what data
 * files reference, so renaming a key is a breaking change.
 */
export const IMAGES = {
  'cheese-board-charcuterie': photo({
    unsplashId: 'qMX3AdGwm0o',
    cdnHash: 'photo-1695606452817-e28a67339e57',
    photographerName: 'Daniel',
    photographerUsername: 'unsplashbydan',
    altText: 'Assorted cheeses and accoutrements on a wooden board',
  }),
  'cheese-sliced-wooden': photo({
    unsplashId: '_L5MGd0w1FQ',
    cdnHash: 'photo-1627935722051-395636b0d8a5',
    photographerName: 'Andra C Taylor Jr',
    photographerUsername: 'taylormadeglobal',
    altText: 'Sliced cheese arranged on a brown wooden chopping board',
  }),
  'cow-mountain-pasture': photo({
    unsplashId: 'G1p0feFHhzo',
    cdnHash: 'photo-1653387131916-698a5f4ec64c',
    photographerName: 'Ramzi Belaidi',
    photographerUsername: 'ramzibelaidi',
    altText: 'Cow grazing in a green pasture with mountains in the distance',
  }),
  'dairy-cow-black-white': photo({
    unsplashId: 'v5GI9rol_WY',
    cdnHash: 'photo-1446126102442-f6b2b73257fd',
    photographerName: 'Angelina Litvin',
    photographerUsername: 'linalitvina',
    altText: 'A black and white dairy cow standing on green grass',
  }),
  'goat-portrait': photo({
    unsplashId: 'xqCEMocugns',
    cdnHash: 'photo-1673622657602-3d1e37b068a8',
    photographerName: 'Andy Bodemer',
    photographerUsername: 'andybodemer',
    altText: 'Close-up portrait of a goat looking at the camera',
  }),
  'goat-on-grass': photo({
    unsplashId: '0b0lC3ddQ-w',
    cdnHash: 'photo-1694644566527-557c4d6699b9',
    photographerName: 'Rin Tian',
    photographerUsername: 'rrrrin_t',
    altText: 'A single goat standing in a sunlit field of grass',
  }),
  'red-barn-silo': photo({
    unsplashId: 'RBT7PPa-rZg',
    cdnHash: 'photo-1712252051217-a921ad205b35',
    photographerName: 'Roger Starnes Sr',
    photographerUsername: 'rstar50',
    altText: 'A red barn with white trim, a white rail fence, and an adjacent silo',
  }),
  'red-barn-large': photo({
    unsplashId: 'OMtg_VvIglU',
    cdnHash: 'photo-1724113712316-634d364f1b38',
    photographerName: 'Roger Starnes Sr',
    photographerUsername: 'rstar50',
    altText: 'A large old red barn with a white roof in an open field',
  }),
  'white-farmhouse-porch': photo({
    unsplashId: 'pGH1-iHveX0',
    cdnHash: 'photo-1761013320045-d29e4f10bcbc',
    photographerName: 'Roger Starnes Sr',
    photographerUsername: 'rstar50',
    altText: 'A large white farmhouse with dormers and a wrap-around porch',
  }),
  'lighthouse-rocky-coast': photo({
    unsplashId: 'MfYoFXv1Zc0',
    cdnHash: 'photo-1665998395404-5b4da44728d3',
    photographerName: 'Rich Martello',
    photographerUsername: 'rmarte',
    altText: 'A lighthouse on a rocky stretch of the Maine coast',
  }),
  'lighthouse-cape-elizabeth': photo({
    unsplashId: 'pwK9XGRyGL8',
    cdnHash: 'photo-1696363204987-1d435252a9a7',
    photographerName: 'Andrew Ling',
    photographerUsername: 'linginit',
    altText: 'Portland Head Light above the rocky shore of Cape Elizabeth, Maine',
  }),
  'lake-champlain-boats': photo({
    unsplashId: '3LOb8a89Y8I',
    cdnHash: 'photo-1721951966000-f30df59d2651',
    photographerName: 'Ronan Furuta',
    photographerUsername: 'ronan18',
    altText: 'Sailboats on Lake Champlain at sunset, photographed from Burlington, Vermont',
  }),
  'portland-cobblestone': photo({
    unsplashId: 'NQt8IWN04J0',
    cdnHash: 'photo-1726055655049-55e4562c7e29',
    photographerName: 'Erwan Hesry',
    photographerUsername: 'erwanhesry',
    altText: 'Cobblestone street lined with brick buildings, reminiscent of Portland Maine’s Old Port',
  }),
} as const;

export type ImageKey = keyof typeof IMAGES;

/**
 * Resolve an image key to a ready-to-use Unsplash CDN URL, sized for our
 * 3:2 card thumbnails (~1200px is plenty for hi-dpi screens).
 */
export function imageUrl(key: ImageKey, opts: { w?: number; q?: number } = {}): string {
  const { w = 1200, q = 80 } = opts;
  const meta = IMAGES[key];
  const base = meta.cdnHash.startsWith('http')
    ? meta.cdnHash
    : `https://images.unsplash.com/${meta.cdnHash}`;
  return `${base}?auto=format&fit=crop&w=${w}&q=${q}`;
}

/** Convenience: get attribution metadata for a given key. */
export function imageCredit(key: ImageKey): {
  photographerName: string;
  photographerUrl: string;
  unsplashUrl: string;
  altText: string;
} {
  const meta = IMAGES[key];
  return {
    photographerName: meta.photographerName,
    photographerUrl: `https://unsplash.com/@${meta.photographerUsername}?utm_source=curd_compass&utm_medium=referral`,
    unsplashUrl: `https://unsplash.com/photos/${meta.unsplashId}?utm_source=curd_compass&utm_medium=referral`,
    altText: meta.altText,
  };
}
